<script setup lang="ts" generic="T extends NxTAccountRow">
/**
 * A T-account („T-Konto“): the lines of a posting — or of an account — on two
 * sides, debit („Soll“) on the left and credit („Haben“) on the right, under
 * one strong rule with one vertical rule between the sides that runs down
 * through the totals. Each side lists its rows on its own (the sides do not
 * pair up), and the totals row closes both sides on one line.
 *
 * `debit` and `credit` hold `{ key, code?, label?, amount?, sub? }` and
 * whatever the slot needs: the account number (monospace), its name and the
 * amount on the right edge. `sub` marks a line that belongs to the row above
 * (a pass-through „Durchbuchung“): indented, muted, in italics, usually
 * without an amount. Amounts and totals are shown as given — the caller
 * formats them, and the totals come with the data: the kit adds nothing up.
 *
 * `caption` names the T above the side letters; `debitTotal` / `creditTotal`
 * fill the totals row, which is left out when both are absent. The slot
 * `label` replaces a row's name and gets `{ row, side, index }`. Whether the
 * posting balances is the caller's to say (a banner under the T).
 */
import { inject } from 'vue';
import { i18nInjectionKey } from '../internal';
import { NxTabularNumber } from '../TabularNumber';

const props = defineProps<{
  /** The debit rows, left. */
  debit: readonly T[];
  /** The credit rows, right. */
  credit: readonly T[];
  /** The heading above the side letters, e.g. „Buchungssatz · Beleg RE-1 · 01.07.2026“. */
  caption?: string;
  /** The debit total, already formatted. */
  debitTotal?: string | number | null;
  /** The credit total, already formatted. */
  creditTotal?: string | number | null;
}>();

defineSlots<{
  label?: (props: { row: T; side: NxTAccountSide; index: number }) => unknown;
}>();

const i18n = inject(i18nInjectionKey, null);
function tr(key: string, fallback: string): string {
  const full = `common.t_account.${key}`;
  const text = i18n?.t(full);
  return text && text !== full ? text : fallback;
}

const hasTotals = () => props.debitTotal != null || props.creditTotal != null;
</script>

<script lang="ts">
export type NxTAccountSide = 'debit' | 'credit';

export interface NxTAccountRow {
  /** A stable key per row. */
  key: string | number;
  /** The account number, set in monospace. */
  code?: string;
  /** The account's name; the slot `label` replaces it. */
  label?: string;
  /** The amount, already formatted; a sub row usually has none. */
  amount?: string | number | null;
  /** A line belonging to the row above (a pass-through): indented, muted, italic. */
  sub?: boolean;
}
</script>

<template>
  <div class="nx-t-account" data-testid="nx-t-account">
    <div v-if="caption" class="nx-t-account__caption">{{ caption }}</div>
    <div class="nx-t-account__grid">
      <div class="nx-t-account__head is-debit" aria-hidden="true">
        {{ tr('debit', 'Soll') }}
      </div>
      <div class="nx-t-account__head is-credit" aria-hidden="true">
        {{ tr('credit', 'Haben') }}
      </div>
      <template v-for="side in ['debit', 'credit'] as const" :key="side">
        <ul
          class="nx-t-account__side"
          :class="side === 'debit' ? 'is-debit' : 'is-credit'"
          :aria-label="side === 'debit' ? tr('debit', 'Soll') : tr('credit', 'Haben')"
          :data-testid="`nx-t-account-${side}`"
        >
          <li
            v-for="(row, index) in side === 'debit' ? debit : credit"
            :key="row.key"
            class="nx-t-account__row"
            :class="{ 'is-sub': row.sub }"
            data-testid="nx-t-account-row"
          >
            <span class="nx-t-account__code">{{ row.code }}</span>
            <span class="nx-t-account__label">
              <slot name="label" :row="row" :side="side" :index="index">{{ row.label }}</slot>
            </span>
            <span class="nx-t-account__amount">
              <NxTabularNumber v-if="row.amount != null" :value="row.amount" />
            </span>
          </li>
        </ul>
      </template>
      <template v-if="hasTotals()">
        <div class="nx-t-account__total is-debit" data-testid="nx-t-account-total-debit">
          <span>{{ tr('debit_total', 'Σ Soll') }}</span>
          <NxTabularNumber v-if="debitTotal != null" :value="debitTotal" />
        </div>
        <div class="nx-t-account__total is-credit" data-testid="nx-t-account-total-credit">
          <span>{{ tr('credit_total', 'Σ Haben') }}</span>
          <NxTabularNumber v-if="creditTotal != null" :value="creditTotal" />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.nx-t-account {
  /* The rules of the T: one weight for the bar and the stem. */
  --t-account-rule: 2px solid var(--nxt-heading-fg);

  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
}

.nx-t-account__caption {
  text-align: center;
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-heading-fg);
}

/* Two equal sides; the head letters, the rows and the totals each take one
   grid row, so the stem runs from the bar through the totals unbroken. */
.nx-t-account__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.nx-t-account__head {
  padding-block-end: var(--nxt-space-2xs);
  text-align: center;
  font-weight: var(--nxt-font-weight-semibold);
  font-size: var(--nxt-font-size-md);
  color: var(--nxt-heading-fg);
}

.nx-t-account__side {
  margin: 0;
  min-height: calc(3 * var(--nxt-control-size-lg));
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  list-style: none;
  border-block-start: var(--t-account-rule);
}

.nx-t-account__side.is-debit,
.nx-t-account__total.is-debit {
  border-inline-end: var(--t-account-rule);
}

/* number | name | amount on the right edge; every row as tall as a large
   control, like the rows of NxTotalsList. */
.nx-t-account__row {
  display: grid;
  grid-template-columns: 5em minmax(0, 1fr) auto;
  align-items: center;
  column-gap: var(--nxt-space-sm);
  min-height: var(--nxt-control-size-lg);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
  line-height: var(--nxt-line-height-tight);
}

.nx-t-account__code {
  font-family: var(--nxt-font-mono);
}

.nx-t-account__label {
  min-width: 0;
}

.nx-t-account__amount {
  text-align: end;
  font-weight: var(--nxt-font-weight-medium);
}

.nx-t-account__row.is-sub {
  color: var(--nxt-fg-subtle);
  font-style: italic;
}

.nx-t-account__row.is-sub .nx-t-account__label {
  padding-inline-start: var(--nxt-space-sm);
}

.nx-t-account__total {
  display: flex;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border-strong);
  font-weight: var(--nxt-font-weight-semibold);
}
</style>
